import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps } from 'types';
import {
  getLGABoundariesOptionStart,
  getLGABoundaryStart,
  updateGeoJsonFromBoundary
} from 'redux/areaOfInterest/actions';
import {
  Button,
  Select,
  SelectItem,
  SelectSkeleton,
  Loading
} from 'carbon-components-react';

const stateMapping = [
  {
    dbydRegion: 'DBYD Queensland',
    state: ['QLD']
  },
  {
    dbydRegion: 'DBYD QLD',
    state: ['QLD']
  },
  {
    dbydRegion: 'DBYD Western Australia',
    state: ['WA']
  },
  {
    dbydRegion: 'DBYD WA',
    state: ['WA']
  },
  {
    dbydRegion: 'DBYD VIC/TAS',
    state: ['VIC', 'TAS']
  },
  {
    dbydRegion: 'DBYD SA/NT',
    state: ['SA', 'NT']
  },
  {
    dbydRegion: 'DBYD NSW/ACT',
    state: ['NSW', 'ACT']
  }
];

const getBoundaryState = (dbydRegion: string) => {
  const mapping = stateMapping.find((s: any) => {
    return s.dbydRegion === dbydRegion;
  });

  return mapping?.state || [];
};

const CommonBoundariesPanel: React.FC<Props> = ({
  orgProfile,
  commonBoundariesJson,
  uploadState,
  boundariesOptions,
  onPanelClose
}) => {
  const [commonBoundary, setCommonBoundary] = useState<string>('-');
  const [boundaryState, setBoundaryState] = useState<string[]>([]);
  const dispatch = useDispatch();

  const preview = () => {
    dispatch(getLGABoundaryStart(commonBoundary));
  };

  const cancel = async () => {
    onPanelClose();
  };

  useEffect(() => {
    (!Array.isArray(boundariesOptions) || boundariesOptions.length === 0) &&
      boundaryState.length > 0 &&
      dispatch(getLGABoundariesOptionStart(boundaryState));
  }, [boundariesOptions, boundaryState, dispatch]);

  useEffect(() => {
    if (orgProfile?.Tenant?.name) {
      const state = getBoundaryState(orgProfile.Tenant.name);
      setBoundaryState(state);
    }
  }, [orgProfile]);

  useEffect(() => {
    if (commonBoundariesJson) {
      dispatch(updateGeoJsonFromBoundary(commonBoundariesJson));
      onPanelClose();
    }
  }, [commonBoundariesJson, dispatch, onPanelClose]);

  return (
    <>
      <div className="aoi-right-panel --swx-theme--dark">
        <div className="panel-description">
          Choose your Area of Interest from the list of Local Government Area
          boundaries
        </div>
        {uploadState === 'processing' && (
          <div className="loading-container panel-divider">
            <Loading />
          </div>
        )}
        <div className="common-boundaries-selector panel-divider">
          {Array.isArray(boundariesOptions) && boundariesOptions.length > 0 ? (
            <Select
              defaultValue={commonBoundary}
              id="aoi-common-boundaries-select"
              labelText="Boundary"
              onChange={(evt: any) => setCommonBoundary(evt.target.value)}
            >
              <SelectItem
                disabled
                hidden
                value="-"
                text=" Choose an area ..."
              />
              {boundariesOptions.map((boundary: any, inx: number) => {
                return (
                  <SelectItem
                    key={`common-boundary-${inx}`}
                    text={boundary.label}
                    value={boundary.value}
                  />
                );
              })}
            </Select>
          ) : (
            <SelectSkeleton />
          )}
        </div>
      </div>
      <div className="aoi-right-panel-button-container">
        <Button
          kind="secondary"
          onClick={() => {
            cancel();
          }}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={() => {
            preview();
          }}
          disabled={commonBoundary === '-'}
        >
          Preview
        </Button>
      </div>
    </>
  );
};

type ownProps = {
  onPanelClose: () => void;
};

const mapStateToProps = ({ aoiCache, memberCache }: RootState) => ({
  orgProfile: memberCache.orgProfile,
  commonBoundariesJson: aoiCache.geoJsonFromLGA,
  uploadState: aoiCache.uploadState,
  boundariesOptions: aoiCache.lgaOptions
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

CommonBoundariesPanel.displayName = 'AOICommonBoundariesPanel';

export default React.memo(connector(CommonBoundariesPanel));
